import { authService } from './auth';

export interface Plan {
  id: string;
  name: string;
  price: number;
  features: string[];
  interval: string;
  trial_days: number;
  is_current: boolean;
  requires_payment: boolean;
}

export interface SubscriptionStatus {
  has_subscription: boolean;
  current_plan: Plan;
  available_plans: Plan[];
  is_on_trial: boolean;
  trial_ends_at: string | null;
  trial_days_remaining: number;
  feature_limits: {
    service_listings: number | null;
    gallery_images: number | null;
  };
}

// Mock plans for demonstration purposes
const mockPlans: Plan[] = [
  {
    id: 'free',
    name: 'Free',
    price: 0,
    features: [
      'Basic profile',
      'Directory listing',
      'Single image upload',
      'Up to 2 service listings',
      'Booking link',
    ],
    interval: 'month',
    trial_days: 0,
    is_current: false,
    requires_payment: false
  },
  {
    id: 'premium',
    name: 'Premium',
    price: 49,
    features: [
      'Everything in Free plan',
      '10+ gallery images',
      'Unlimited service listings',
      'Phone number always visible',
      'Full analytics with visitor insights',
      'Featured directory card with image',
    ],
    interval: 'month',
    trial_days: 14,
    is_current: false,
    requires_payment: true
  }
];

export const subscriptionService = {
  async getStatus(): Promise<SubscriptionStatus> {
    // For demonstration, we'll return mock data
    // In a real implementation, this would call your API
    
    const currentPlanId = localStorage.getItem('user_plan') || 'free';
    
    const plans = mockPlans.map(plan => ({
      ...plan,
      is_current: plan.id === currentPlanId
    }));
    
    const currentPlan = plans.find(p => p.is_current) || plans[0];
    
    // Mock limits based on plan
    const featureLimits = {
      service_listings: currentPlanId === 'free' ? 2 : null,
      gallery_images: currentPlanId === 'free' ? 0 : null,
    };
    
    return {
      has_subscription: currentPlanId !== 'free',
      current_plan: currentPlan,
      available_plans: plans,
      is_on_trial: false,
      trial_ends_at: null,
      trial_days_remaining: 0,
      feature_limits: featureLimits
    };
  },

  async upgrade(planId: string): Promise<{ checkout_url: string; trial_days: number }> {
    // In a real implementation, this would create a Stripe Checkout session
    // For demonstration, we'll just update the user's plan in localStorage
    localStorage.setItem('user_plan', planId);
    
    const plan = mockPlans.find(p => p.id === planId);
    
    // In a real app, this would return a Stripe checkout URL
    return {
      checkout_url: '/subscription-success',
      trial_days: plan?.trial_days || 0
    };
  },

  async cancel(): Promise<{ message: string; ends_at: string }> {
    // In a real implementation, this would cancel the subscription in Stripe
    localStorage.setItem('user_plan', 'free');
    
    return {
      message: 'Subscription cancelled successfully',
      ends_at: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString() // 30 days from now
    };
  },

  async resume(): Promise<{ message: string }> {
    // In a real implementation, this would resume the subscription in Stripe
    // For simplicity, we'll assume resuming to 'premium'
    localStorage.setItem('user_plan', 'premium');
    
    return {
      message: 'Subscription resumed successfully'
    };
  },
};
